import useSessionTimeout from "../../hooks/useSessionTimeout";
import { Box, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, VStack } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { Button } from "newui/components/Button";
import { useEffect, useRef, useState } from "react";
import { isNullOrUndefined } from "newui/utils/objectUtil";
import { useCurrentUser } from "hooks/auth";
import { useAuthContext } from "../../context/AuthContextProvider";

const SessionTimeoutModal: React.FC = () => {
	const { extendSession, expiryTime, warningThreshold, updateSessionExpiry } = useSessionTimeout();
	const { currentUser } = useCurrentUser();
	const { logoutUser } = useAuthContext();
	const [now, setNow] = useState(Date.now());
	const [logoutClicked, setLogoutClicked] = useState(false);

	// for when we need to update timers
	const timeoutRef = useRef<number | null>(null);

	// for when we have crossed the warning threshold
	const intervalRef = useRef<number | null>(null);

	// Snap to real time when tab becomes visible (handles hibernation/sleep)
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				setNow(Date.now());
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	// Smart ticking
	useEffect(() => {
		if (!currentUser || isNullOrUndefined(expiryTime)) {
			return;
		}

		// clear timeout and intervals
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		const timeLeftNow = Math.max(expiryTime - Date.now(), 0);

		if (timeLeftNow <= 0) {
			setNow(Date.now());
			return;
		}

		if (timeLeftNow > warningThreshold) {
			const msUntilWarning = timeLeftNow - warningThreshold;
			timeoutRef.current = window.setTimeout(() => {
				setNow(Date.now());
			}, msUntilWarning);
		} else if (timeLeftNow > 0) {
			intervalRef.current = window.setInterval(() => {
				setNow(Date.now());
			}, 1000);
		}

		return () => {
			if (timeoutRef.current) { clearTimeout(timeoutRef.current); }
			if (intervalRef.current) { clearInterval(intervalRef.current); }
			timeoutRef.current = null;
			intervalRef.current = null;
		};
	}, [currentUser, expiryTime, now, warningThreshold]);

	if (!expiryTime || !currentUser) {
		return null;
	}

	const timeLeftMs = expiryTime - now;
	const hasExpired = timeLeftMs <= 0;
	const showWarning = !hasExpired && timeLeftMs <= warningThreshold;

	if (!showWarning && !hasExpired) {
		return null;
	}

	const minutes = Math.floor(timeLeftMs / 60000);
	const seconds = Math.floor((timeLeftMs % 60000) / 1000);

	const handleLogoutWithValidSession = () => {
		setLogoutClicked(true);
	};

	const handleLogout = () => {
		updateSessionExpiry(null);
		logoutUser();
	}

	return (
		<Modal
			isOpen={true}
			onClose={() => {
				// do nothing
			}}
			isCentered
			closeOnOverlayClick={false}
			closeOnEsc={false}
			motionPreset="scale"
			size="lg" // fixed size for desktop
		>
			<ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
			<ModalContent
				borderRadius="xl"
				bg="white"
				boxShadow="2xl"
				maxW="500px" // desktop modal width
				mx="auto"
			>
				<ModalHeader
					pt={8}
					pb={4}
					px={6}
					borderBottom="1px solid"
					borderColor="gray.100"
				>
					<VStack spacing={3} align="center">
						<Box
							p={3}
							borderRadius="full"
							bg={hasExpired ? 'red.50' : 'orange.50'}
						>
							<Icon
								icon={'alert-triangle'}
								iconClassName="fill-yellow-200"
								color={hasExpired ? 'red.500' : 'orange.500'}
								style={{ width: 50, height: 50 }}
							/>
						</Box>
						<Text fontSize="2xl" fontWeight="bold" color={hasExpired ? "red.800" : "yellow.600"} textAlign="center">
							{hasExpired ? "Session Expired" : "Session Timeout Warning"}
						</Text>
					</VStack>
				</ModalHeader>

				<ModalBody px={2} py={8}>
					<VStack spacing={6} align="stretch">
						{hasExpired ? (
							<><Text fontSize="lg" color="gray.700" textAlign="center" lineHeight="tall">
								For your security, you have been logged out due to inactivity
							</Text>
								<Text fontSize="lg" color="gray.700" textAlign="center" lineHeight="tall">
									Please login again to continue.
								</Text>
							</>

						) : (
							<>
								<Box
									p={4}
								>
									<Text fontSize="xl" fontWeight="semibold" color="gray.800" textAlign="center">
										{minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `0:${seconds.toString().padStart(2, '0')}s`}
									</Text>
									<Text fontSize="sm" color="gray.600" textAlign="center">
										remaining
									</Text>
								</Box>
								{logoutClicked ? <Text fontSize="lg" color="gray.700" textAlign="center" fontWeight={"extrabold"}>
									Are you sure you want to logout?
								</Text> : <>
									<Text fontSize="md" color="gray.700" textAlign="center">
										Click <strong>Stay Connected</strong> to continue working.
									</Text>
								</>
								}
								<Text fontSize={logoutClicked ? "lg" : "sm"} color="gray.500" textAlign="center">
									Any unsaved changes will be lost {!logoutClicked && "if you don't stay connected."}
								</Text>
							</>
						)}
					</VStack>
				</ModalBody>

				<ModalFooter px={6} pb={6} pt={4} flexDirection="column" gap={3}>
					{hasExpired ? (
						<Button
							onClick={handleLogout}
							btntype="primary"
							size="lg"
							width="full"
						>
							<Icon icon="logout" boxSize={18} className="pr-4" />Login
						</Button>
					) : (
						<>
							<Button
								onClick={extendSession}
								btntype="primary"
								size="lg"
								width="full"
							>
								<Icon icon="refresh" boxSize={18} className="pr-4" style={{ stroke: "black" }} />{logoutClicked && "No - "}Stay Connected
							</Button>
							<Button
								onClick={logoutClicked ? handleLogout : handleLogoutWithValidSession}
								btntype="secondary"
								size="lg"
								width="full"
							>
								<Icon icon="logout" boxSize={18} className="pr-4" />{logoutClicked && "Yes - "}Logout Now
							</Button>
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default SessionTimeoutModal;
